import { motion } from "framer-motion";
import { Clock, Filter, ShieldCheck, TrendingDown } from "lucide-react";
import type { TrustProof, CoverageState } from "@/lib/decisionEngine";

interface Props {
  proof: TrustProof;
  coverage: CoverageState;
  winnerPrice?: string;
}

function estimateTimeSaved(offersChecked: number): string {
  // Conservative: ~30s per manual comparison, minimum 2 min
  const mins = Math.max(2, Math.round(offersChecked * 0.5));
  return mins >= 60 ? `${Math.round(mins / 60)}h+` : `~${mins} min`;
}

export default function ValueProofBar({ proof, coverage, winnerPrice }: Props) {
  const isVerified = coverage === "live" || (coverage === "partial" && proof.offersChecked > 0);
  const hasRealWork = proof.offersChecked > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
      className="w-full max-w-xl mx-auto mb-4"
    >
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] font-body text-muted-foreground">
        {hasRealWork && (
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3 text-score-high" />
            <span className="text-foreground font-medium">{proof.offersChecked}</span> offers verified
          </span>
        )}
        {proof.removed > 0 && (
          <span className="flex items-center gap-1.5">
            <Filter className="w-3 h-3 text-score-mid" />
            <span className="text-foreground font-medium">{proof.removed}</span> weak options removed
          </span>
        )}
        {hasRealWork && (
          <span className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-primary" />
            {estimateTimeSaved(proof.offersChecked)} of research saved
          </span>
        )}
        {!hasRealWork && (
          <span className="flex items-center gap-1.5">
            <TrendingDown className="w-3 h-3 text-muted-foreground" />
            AI-analyzed from current market data
          </span>
        )}
      </div>
    </motion.div>
  );
}
